<script setup lang="ts">
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { ref, onMounted, onBeforeUnmount, reactive, computed } from 'vue';
import { useSlots } from 'vue';

withDefaults(
  defineProps<{
    maxHeight?: string;
    disableFadeTop?: boolean;
    disableFadeBottom?: boolean;
    scrollClass?: string;
    /** Gradient start of the fades, matching the background behind the list. */
    fadeFromClass?: string;
  }>(),
  {
    maxHeight: undefined,
    scrollClass: undefined,
    fadeFromClass: 'from-surface-secondary',
  },
);
const slots = useSlots();
let resizeObserver: ResizeObserver | null = null;
const scrollRef = ref<HTMLElement | null>(null);
const initialBottom = ref<boolean>(true);
const hasScrollableContent = ref(false);
const scrollIndicators = reactive({
  showTop: false,
  showBottom: true,
});
const hasHeaderSlot = computed(() => !!slots.header);

const updateScrollIndicators = (): void => {
  const el = scrollRef.value;
  if (!el) return;
  hasScrollableContent.value = el.scrollHeight > el.clientHeight;
  scrollIndicators.showTop = el.scrollTop > 0;
  scrollIndicators.showBottom = el.scrollTop + el.clientHeight < el.scrollHeight;
  if (scrollIndicators.showBottom) initialBottom.value = false;
};

onMounted(() => {
  const el = scrollRef.value;
  if (el) {
    updateScrollIndicators();
    el.addEventListener('scroll', updateScrollIndicators);
    resizeObserver = new ResizeObserver(updateScrollIndicators);
    resizeObserver.observe(el);
  }
});

onBeforeUnmount(() => {
  scrollRef.value?.removeEventListener('scroll', updateScrollIndicators);
  resizeObserver?.disconnect();
});
</script>

<template>
  <div class="relative flex min-h-0 flex-col overflow-hidden rounded-md">
    <div v-if="hasScrollableContent && scrollIndicators.showTop && !hasHeaderSlot">
      <div class="absolute top-0 left-0 right-0 flex justify-center pointer-events-none z-10">
        <div
          :class="[
            !disableFadeTop && ['bg-gradient-to-b to-transparent', fadeFromClass],
            'w-full h-8 flex items-center justify-center',
            disableFadeTop && 'pb-4',
          ]"
        >
          <ChevronDownIcon class="size-icon text-tint rotate-180" />
        </div>
      </div>
    </div>

    <div class="sticky top-0 shrink-0 bg-white z-9 rounded-md">
      <slot name="header" />
      <div v-if="hasScrollableContent && scrollIndicators.showTop && hasHeaderSlot">
        <div class="absolute bottom-0 left-0 right-0 flex justify-center pointer-events-none z-10">
          <div
            :class="[
              !disableFadeTop && ['bg-gradient-to-b to-transparent', fadeFromClass],
              'w-full h-8 flex items-center justify-center',
              disableFadeTop && 'pb-4',
            ]"
          >
            <ChevronDownIcon class="size-icon text-tint rotate-180" />
          </div>
        </div>
      </div>
    </div>

    <div
      ref="scrollRef"
      :class="['min-h-0 overflow-y-auto overscroll-contain relative z-0 max-h-80', scrollClass]"
      :style="{ maxHeight: maxHeight }"
    >
      <slot />
    </div>

    <div v-if="hasScrollableContent && (initialBottom || scrollIndicators.showBottom)">
      <div class="absolute bottom-0 left-0 right-0 flex justify-center pointer-events-none z-10">
        <div
          :class="[
            !disableFadeBottom && ['bg-gradient-to-t to-transparent', fadeFromClass],
            'w-full h-8 flex items-center justify-center',
          ]"
        >
          <ChevronDownIcon class="size-icon text-tint" />
        </div>
      </div>
    </div>
  </div>
</template>
